import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiPlus, FiExternalLink, FiTrash2, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import {
  CalendarTimeline,
  type CalendarBar,
  type CalendarRow,
  type VariantStyle,
} from './CalendarTimeline';
import { buildCalendarAxis } from './calendarAxis';
import { useDatabase } from '../../hooks/useDatabase';
import { useWorkspace } from '../../context/WorkspaceContext';
import type { WorkspaceUrlTarget } from '../../routes/workspaceUrl';
import { useResourceReservations, type ReservationDetails } from '../../hooks/useResourceReservations';
import ResourceReservationForm from '../forms/ResourceReservationForm';
import NativeResourceForm from '../forms/NativeResourceForm';
import NativeResourceGroupForm from '../forms/NativeResourceGroupForm';
import EntitySearchAndCreate from '../common/EntitySearchAndCreate';

/**
 * Reservation timeline for resources managed by a given team. Shows one row
 * per resource with horizontal bars for each reservation in the window.
 *
 * Reuses `CalendarTimeline` so the period bands, weekend shading, and today
 * line match the team's Schedule panel.
 *
 * Add affordances:
 *  - "+ Resource" — find existing or create new; auto-link to this team via
 *    `managed_by_team`.
 *  - "+ Group"    — find existing or create new resource group; auto-link to
 *    this team. Groups themselves don't show on the calendar (they have no
 *    reservations of their own), but they're surfaced as chips below it.
 */

interface Props {
  /**
   * Team that "owns" the team-specific affordances (+ Resource / + Group
   * pickers, group chips below the calendar). Optional — when absent the
   * affordances are hidden and the panel is purely a reservation calendar.
   */
  teamId?: string;
  /**
   * Explicit list of resource ids to render rows for. When absent, falls
   * back to all resources whose `managed_by_team` matches `teamId`.
   */
  resourceIds?: string[];
  /** ISO date — start of the displayed window. */
  startDate: string;
  /** ISO date — exclusive end of the displayed window. */
  endDate: string;
  /** Number of days per period in the calendar axis (matches team rhythm). */
  intervalDays?: number;
  /** Workspace id for resource detail links. */
  workspaceId: string | undefined;
  /** Optional override for the empty-state message. */
  emptyMessage?: string;
}

type ReservationVariant = 'reserved' | 'blocked';

const VARIANT_STYLES: Record<ReservationVariant, VariantStyle> = {
  reserved: {
    idle: 'bg-cyan-500/60 text-white',
    hover: 'bg-cyan-500/80 text-white',
  },
  blocked: {
    idle: 'bg-gray-500/40 text-gray-200 border border-dashed border-gray-400/50',
    hover: 'bg-gray-500/60 text-gray-200 border border-dashed border-gray-400/50',
  },
};

/**
 * Resolve a reservation's `object` (a `NodeMatcher`) to a human label and an
 * optional in-app link. Returns `null` for an unknown shape so the caller can
 * fall back to a generic label.
 */
function resolveObject(
  object: any,
  database: any,
  buildUrl: (target: WorkspaceUrlTarget) => string,
): { label: string; linkTo?: string } | null {
  if (!object || typeof object.type !== 'string') return null;
  switch (object.type) {
    case 'TaskId': {
      const title = database?.tasks?.()?.[object.id]?.title?.() ?? `Task ${String(object.id).slice(0, 8)}`;
      return { label: title, linkTo: buildUrl({ kind: 'task', taskId: object.id }) };
    }
    case 'OutcomeId': {
      const title = database?.outcomes?.()?.[object.id]?.title?.() ?? `Outcome ${String(object.id).slice(0, 8)}`;
      return { label: title, linkTo: buildUrl({ kind: 'outcome', outcomeId: object.id }) };
    }
    case 'ProblemId': {
      const title = database?.problems?.()?.[object.id]?.title?.() ?? `Problem ${String(object.id).slice(0, 8)}`;
      return { label: title, linkTo: buildUrl({ kind: 'problem', problemId: object.id }) };
    }
    case 'ProjectId': {
      const title = database?.projects?.()?.[object.id]?.name?.() ?? `Project ${String(object.id).slice(0, 8)}`;
      return { label: title, linkTo: buildUrl({ kind: 'project', projectId: object.id }) };
    }
    case 'Tag':
      return { label: `#${object.tag}` };
    case 'Logic':
      return { label: 'Multiple matchers' };
    default:
      return null;
  }
}

/** Return reservation's first and last day index within the window, or null if fully outside. */
function reservationBoundsInWindow(
  when: any,
  daysByIso: Record<string, number>,
  days: { date: string }[],
): { startIdx: number; endIdx: number } | null {
  if (!when) return null;
  const start = when.type === 'Day' ? when.day : when.start_inclusive;
  const end = when.type === 'Day' ? when.day : when.end_inclusive;
  if (!start || !end) return null;

  // Clamp to the window.
  const firstInWindow = days[0]?.date;
  const lastInWindow = days[days.length - 1]?.date;
  if (!firstInWindow || !lastInWindow) return null;
  if (end < firstInWindow || start > lastInWindow) return null;

  const clampedStart = start < firstInWindow ? firstInWindow : start;
  const clampedEnd = end > lastInWindow ? lastInWindow : end;

  const startIdx = daysByIso[clampedStart];
  const endIdx = daysByIso[clampedEnd];
  if (startIdx === undefined || endIdx === undefined) return null;
  return { startIdx, endIdx };
}

// ── Add picker (popover) ────────────────────────────────────────────

type AddPickerKind = 'resource' | 'group' | null;

function AddPickerPopover({
  kind,
  candidates,
  anchorClassName,
  onPickExisting,
  onCreateNew,
  onClose,
}: {
  kind: Exclude<AddPickerKind, null>;
  candidates: { id: string; name: string }[];
  anchorClassName?: string;
  onPickExisting: (id: string) => void;
  onCreateNew: (name: string) => void;
  onClose: () => void;
}) {
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handle = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [onClose]);

  return (
    <div
      ref={popoverRef}
      className={`absolute right-0 mt-1 w-80 z-30 bg-cm-bg-elevated border border-cm-border-mid rounded-lg shadow-xl p-3 ${anchorClassName ?? ''}`}
    >
      <h4 className="text-xs font-mono text-cm-text-secondary mb-2">
        Add {kind === 'resource' ? 'a resource' : 'a resource group'} to this team
      </h4>
      <EntitySearchAndCreate
        candidates={candidates}
        onPickExisting={(id) => {
          onPickExisting(id);
          onClose();
        }}
        onCreateNew={(name) => {
          onCreateNew(name);
          onClose();
        }}
        createLabel={kind === 'resource' ? 'Create new resource' : 'Create new group'}
        placeholder={
          kind === 'resource' ? 'Find or create a resource…' : 'Find or create a group…'
        }
        autoFocus
        compact
      />
    </div>
  );
}

// ── Detail modal ────────────────────────────────────────────────────

function whenSummary(when: any): string {
  if (!when) return '—';
  if (when.type === 'Day') return when.day;
  if (when.type === 'Days') return `${when.start_inclusive} → ${when.end_inclusive}`;
  return '—';
}

function ReservationDetailModal({
  reservation,
  resourceName,
  buildUrl,
  database,
  onClose,
  onDelete,
}: {
  reservation: ReservationDetails;
  resourceName: string;
  buildUrl: (target: WorkspaceUrlTarget) => string;
  database: any;
  onClose: () => void;
  onDelete: () => Promise<void>;
}) {
  const resolved = reservation.isBlock
    ? null
    : resolveObject(reservation.object, database, buildUrl);
  const resourceLink = buildUrl({ kind: 'resource', resourceId: reservation.resourceId });

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-cm-bg-surface p-6 rounded-lg shadow-lg w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-xl font-semibold text-cm-text-primary">
            {reservation.isBlock ? 'Block' : 'Reservation'}
          </h2>
          <button
            onClick={onClose}
            className="text-cm-text-muted hover:text-cm-text-primary"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <dl className="space-y-3 text-sm">
          <div>
            <dt className="text-xs font-mono text-cm-text-muted">Resource</dt>
            <dd className="mt-0.5">
              {resourceLink ? (
                <Link
                  to={resourceLink}
                  className="text-cm-accent-blue hover:underline inline-flex items-center gap-1"
                >
                  {resourceName}
                  <FiExternalLink className="w-3 h-3" />
                </Link>
              ) : (
                resourceName
              )}
            </dd>
          </div>

          <div>
            <dt className="text-xs font-mono text-cm-text-muted">When</dt>
            <dd className="mt-0.5 text-cm-text-primary">{whenSummary(reservation.when)}</dd>
          </div>

          {!reservation.isBlock && (
            <div>
              <dt className="text-xs font-mono text-cm-text-muted">Reserved for</dt>
              <dd className="mt-0.5">
                {resolved?.linkTo ? (
                  <Link
                    to={resolved.linkTo}
                    className="text-cm-accent-blue hover:underline inline-flex items-center gap-1"
                  >
                    {resolved.label}
                    <FiExternalLink className="w-3 h-3" />
                  </Link>
                ) : (
                  <span className="text-cm-text-primary">{resolved?.label ?? 'Reserved'}</span>
                )}
              </dd>
            </div>
          )}

          {reservation.remark && (
            <div>
              <dt className="text-xs font-mono text-cm-text-muted">Note</dt>
              <dd className="mt-0.5 text-cm-text-primary whitespace-pre-wrap">{reservation.remark}</dd>
            </div>
          )}
        </dl>

        <div className="mt-6 flex justify-between">
          <button
            type="button"
            onClick={onDelete}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs text-red-400 hover:text-red-300 border border-red-400/40 hover:border-red-400/70 transition-colors"
          >
            <FiTrash2 className="w-3 h-3" /> Delete
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded text-xs text-cm-text-muted hover:text-cm-text-primary border border-cm-border-subtle hover:border-cm-border-mid transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────────

export const ResourceReservationPanel: React.FC<Props> = ({
  teamId,
  resourceIds,
  startDate: propsStartDate,
  endDate: propsEndDate,
  intervalDays,
  workspaceId,
  emptyMessage,
}) => {
  const { database, applyCommand } = useDatabase();
  const { buildUrl } = useWorkspace();
  const { reservations } = useResourceReservations();

  // ── Window navigation ──────────────────────────────────────────────
  // Caller supplies the "home" window via props. The panel maintains an
  // offset (in days) that prev/next/today buttons mutate. We shift by the
  // window length so each step pages through a full window.
  const [offsetDays, setOffsetDays] = useState(0);

  const windowDays = useMemo(() => {
    const start = new Date(propsStartDate + 'T00:00:00');
    const end = new Date(propsEndDate + 'T00:00:00');
    return Math.max(1, Math.round((end.getTime() - start.getTime()) / 86400000) + 1);
  }, [propsStartDate, propsEndDate]);

  const shiftDate = (iso: string, days: number) => {
    const d = new Date(iso + 'T00:00:00');
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
  };

  const startDate = useMemo(() => shiftDate(propsStartDate, offsetDays), [propsStartDate, offsetDays]);
  const endDate = useMemo(() => shiftDate(propsEndDate, offsetDays), [propsEndDate, offsetDays]);
  const stepBy = (steps: number) => setOffsetDays((o) => o + steps * windowDays);
  const goHome = () => setOffsetDays(0);
  const [reservationForm, setReservationForm] = useState<{
    open: boolean;
    defaultResourceId?: string;
    defaultStartDate?: string;
    defaultEndDate?: string;
  }>({ open: false });
  const [showAddPicker, setShowAddPicker] = useState<AddPickerKind>(null);
  const [selectedReservation, setSelectedReservation] = useState<ReservationDetails | null>(null);

  // Pre-populated create-form state, opened either from the Add picker or
  // (in the case of resource creation) the user directly.
  const [createResourceForm, setCreateResourceForm] = useState<{ open: boolean; defaultName: string }>({
    open: false,
    defaultName: '',
  });
  const [createGroupForm, setCreateGroupForm] = useState<{ open: boolean; defaultName: string }>({
    open: false,
    defaultName: '',
  });

  // ─── Inputs ─────────────────────────────────────────────────────
  const allResources = useMemo(() => {
    if (!database) return [] as { id: string; name: string; managedByTeamId: string | undefined }[];
    return Object.entries(database.resources()).map(([id, r]: [string, any]) => ({
      id,
      name: r.name(),
      managedByTeamId: r.managed_by_team_id() ?? undefined,
    }));
  }, [database]);

  const allGroups = useMemo(() => {
    if (!database) return [] as { id: string; name: string; managedByTeamId: string | undefined }[];
    return Object.entries(database.resource_groups()).map(([id, g]: [string, any]) => ({
      id,
      name: g.name(),
      managedByTeamId: g.managed_by_team_id() ?? undefined,
    }));
  }, [database]);

  /**
   * Resources rendered as rows. Explicit `resourceIds` wins; otherwise the
   * panel filters by team. Sorted to match the explicit order when given,
   * alphabetical otherwise.
   */
  const displayedResources = useMemo(() => {
    if (resourceIds) {
      const byId = new Map(allResources.map((r) => [r.id, r] as const));
      return resourceIds
        .map((id) => byId.get(id))
        .filter((r): r is { id: string; name: string; managedByTeamId: string | undefined } => !!r);
    }
    if (!teamId) return [];
    return allResources
      .filter((r) => r.managedByTeamId === teamId)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allResources, resourceIds, teamId]);

  const teamGroups = useMemo(
    () =>
      teamId
        ? allGroups.filter((g) => g.managedByTeamId === teamId).sort((a, b) => a.name.localeCompare(b.name))
        : [],
    [allGroups, teamId],
  );

  const linkableResources = useMemo(
    () =>
      teamId
        ? allResources.filter((r) => r.managedByTeamId !== teamId).sort((a, b) => a.name.localeCompare(b.name))
        : [],
    [allResources, teamId],
  );

  const linkableGroups = useMemo(
    () =>
      teamId
        ? allGroups.filter((g) => g.managedByTeamId !== teamId).sort((a, b) => a.name.localeCompare(b.name))
        : [],
    [allGroups, teamId],
  );

  // ─── Calendar axis & bars ───────────────────────────────────────
  const { days, periods } = useMemo(
    () => buildCalendarAxis({ startDate, endDate, intervalDays }),
    [startDate, endDate, intervalDays],
  );

  const daysByIso = useMemo(() => {
    const out: Record<string, number> = {};
    days.forEach((d, i) => {
      out[d.date] = i;
    });
    return out;
  }, [days]);

  const reservationsByResource = useMemo(() => {
    const out: Record<string, typeof reservations> = {};
    for (const r of reservations) {
      (out[r.resourceId] ||= []).push(r);
    }
    return out;
  }, [reservations]);

  const rows: CalendarRow[] = useMemo(() => {
    return displayedResources.map((resource) => {
      const reservs = reservationsByResource[resource.id] ?? [];
      const bars: CalendarBar[] = [];
      for (const r of reservs) {
        const bounds = reservationBoundsInWindow(r.when, daysByIso, days);
        if (!bounds) continue;
        const variant: ReservationVariant = r.isBlock ? 'blocked' : 'reserved';
        const resolved = r.isBlock ? null : resolveObject(r.object, database, buildUrl);
        const label = r.isBlock
          ? (r.remark ?? 'Blocked')
          : (resolved?.label ?? 'Reserved');
        bars.push({
          key: r.id,
          label,
          startIdx: bounds.startIdx,
          endIdx: bounds.endIdx,
          variant,
          tooltipSuffix: r.remark && !r.isBlock ? `— ${r.remark}` : undefined,
          resizable: true,
          onClick: () => setSelectedReservation(r),
        });
      }

      return {
        key: resource.id,
        labelContent: (
          <Link
            to={buildUrl({ kind: 'resource', resourceId: resource.id })}
            className="text-xs font-medium truncate hover:text-cm-accent-blue transition-colors"
          >
            {resource.name}
          </Link>
        ),
        bars,
      };
    });
  }, [displayedResources, reservationsByResource, daysByIso, days, database, buildUrl]);

  // ─── Actions ────────────────────────────────────────────────────
  // The team-link helpers are only invoked from team-scoped UI rendered when
  // `teamId` is set; the early returns are belt-and-braces.
  const linkResourceToTeam = (resourceId: string) => {
    if (!teamId) return;
    applyCommand({
      type: 'UpdateResource',
      id: resourceId,
      actions: [{ type: 'SetManagedByTeam', team_id: teamId }],
    });
  };

  const linkGroupToTeam = (groupId: string) => {
    if (!teamId) return;
    applyCommand({
      type: 'UpdateResourceGroup',
      id: groupId,
      actions: [{ type: 'SetManagedByTeam', team_id: teamId }],
    });
  };

  // ─── Drag callbacks ─────────────────────────────────────────────
  // Drag-to-create: open the reservation form pre-filled with the row's
  // resource and the dragged date range. The user fills in remaining details
  // (kind, task, remark) and submits.
  const handleRangeCreate = (rowKey: string, startIdx: number, endIdx: number) => {
    const startDate = days[startIdx]?.date;
    const endDate = days[endIdx]?.date;
    if (!startDate || !endDate) return;
    setReservationForm({
      open: true,
      defaultResourceId: rowKey,
      defaultStartDate: startDate,
      defaultEndDate: endDate,
    });
  };

  // Edge-resize: directly upsert the existing reservation with a new `when`.
  // We preserve `object`, `remark`, and `reserved_by` from the current
  // reservation — only the date range changes.
  const handleRangeResize = (
    _rowKey: string,
    barKey: string,
    startIdx: number,
    endIdx: number,
  ) => {
    const reservation = reservations.find((r) => r.id === barKey);
    if (!reservation) return;
    const startDate = days[startIdx]?.date;
    const endDate = days[endIdx]?.date;
    if (!startDate || !endDate) return;
    const when =
      startDate === endDate
        ? ({ type: 'Day', day: startDate } as const)
        : ({
            type: 'Days',
            start_inclusive: startDate,
            end_inclusive: endDate,
          } as const);
    applyCommand({
      type: 'UpsertResourceReservation',
      reservation: {
        id: reservation.id,
        resourceId: reservation.resourceId,
        when,
        object: reservation.object,
        remark: reservation.remark,
        reservedBy: reservation.reservedBy,
      },
    });
  };

  const navButtonClass =
    'px-2 py-1 rounded text-xs text-cm-text-muted hover:text-cm-text-primary border border-cm-border-subtle hover:border-cm-border-mid transition-colors';
  const isShifted = offsetDays !== 0;

  return (
    <>
      <div className="flex items-center gap-2 mb-3 relative">
        {/* Window navigation */}
        <div className="flex items-center gap-1">
          <button onClick={() => stepBy(-1)} className={navButtonClass} title="Previous window">
            <FiChevronLeft className="w-3 h-3" />
          </button>
          <button
            onClick={goHome}
            className={`${navButtonClass} ${isShifted ? '' : 'opacity-50'}`}
            disabled={!isShifted}
            title="Reset to today"
          >
            Today
          </button>
          <button onClick={() => stepBy(1)} className={navButtonClass} title="Next window">
            <FiChevronRight className="w-3 h-3" />
          </button>
          <span className="text-[11px] font-mono text-cm-text-muted ml-1">
            {startDate} → {endDate}
          </span>
        </div>

        <div className="ml-auto flex items-center gap-2">
          {/* Team-only add affordances */}
          {teamId && (
            <>
              <div className="relative">
                <button
                  onClick={() => setShowAddPicker((prev) => (prev === 'resource' ? null : 'resource'))}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded text-xs text-cm-text-muted hover:text-cm-text-primary border border-cm-border-subtle hover:border-cm-border-mid transition-colors"
                >
                  <FiPlus className="w-3 h-3" /> Resource
                </button>
                {showAddPicker === 'resource' && (
                  <AddPickerPopover
                    kind="resource"
                    candidates={linkableResources}
                    onPickExisting={linkResourceToTeam}
                    onCreateNew={(name) => setCreateResourceForm({ open: true, defaultName: name })}
                    onClose={() => setShowAddPicker(null)}
                  />
                )}
              </div>

              <div className="relative">
                <button
                  onClick={() => setShowAddPicker((prev) => (prev === 'group' ? null : 'group'))}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded text-xs text-cm-text-muted hover:text-cm-text-primary border border-cm-border-subtle hover:border-cm-border-mid transition-colors"
                >
                  <FiPlus className="w-3 h-3" /> Group
                </button>
                {showAddPicker === 'group' && (
                  <AddPickerPopover
                    kind="group"
                    candidates={linkableGroups}
                    onPickExisting={linkGroupToTeam}
                    onCreateNew={(name) => setCreateGroupForm({ open: true, defaultName: name })}
                    onClose={() => setShowAddPicker(null)}
                  />
                )}
              </div>
            </>
          )}

          <button
            onClick={() => setReservationForm({ open: true })}
            disabled={displayedResources.length === 0}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded text-xs text-cm-text-muted hover:text-cm-text-primary border border-cm-border-subtle hover:border-cm-border-mid transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <FiPlus className="w-3 h-3" /> Reservation
          </button>
        </div>
      </div>

      {displayedResources.length === 0 ? (
        <p className="text-sm text-cm-text-muted text-center py-6">
          {emptyMessage ??
            (teamId
              ? 'No resources managed by this team yet. Click + Resource above to add one.'
              : 'No resources to display.')}
        </p>
      ) : (
        <>
          <CalendarTimeline
            days={days}
            periods={periods}
            rows={rows}
            variantStyles={VARIANT_STYLES}
            emptyMessage="No resources to display."
            onRangeCreate={handleRangeCreate}
            onRangeResize={handleRangeResize}
          />
          <p className="mt-2 text-[11px] text-cm-text-muted">
            Click a column to reserve that period; drag to span multiple. Drag a bar's edge to resize.
          </p>
        </>
      )}

      {/* Resource groups managed by this team — surfaced as link chips. */}
      {teamGroups.length > 0 && (
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <span className="text-xs text-cm-text-muted font-mono">Groups:</span>
          {teamGroups.map((g) => (
            <Link
              key={g.id}
              to={buildUrl({ kind: 'resource-group', groupId: g.id })}
              className="px-2 py-0.5 rounded-full text-xs bg-cm-bg-elevated border border-cm-border-subtle text-cm-text-secondary hover:border-cm-accent-teal hover:text-cm-accent-teal transition-colors"
            >
              {g.name}
            </Link>
          ))}
        </div>
      )}

      <ResourceReservationForm
        isOpen={reservationForm.open}
        onClose={() => setReservationForm({ open: false })}
        onSubmit={applyCommand}
        defaultResourceId={reservationForm.defaultResourceId}
        defaultStartDate={reservationForm.defaultStartDate}
        defaultEndDate={reservationForm.defaultEndDate}
      />

      <NativeResourceForm
        isOpen={createResourceForm.open}
        defaultName={createResourceForm.defaultName}
        defaultManagedByTeamId={teamId}
        onClose={() => setCreateResourceForm({ open: false, defaultName: '' })}
        onSubmit={applyCommand}
        // The form already sets `managed_by_team` from the pre-filled field,
        // so no extra link command is needed on `onCreated`.
      />

      <NativeResourceGroupForm
        isOpen={createGroupForm.open}
        defaultName={createGroupForm.defaultName}
        defaultManagedByTeamId={teamId}
        onClose={() => setCreateGroupForm({ open: false, defaultName: '' })}
        onSubmit={applyCommand}
      />

      {selectedReservation && (
        <ReservationDetailModal
          reservation={selectedReservation}
          resourceName={
            displayedResources.find((r) => r.id === selectedReservation.resourceId)?.name ??
            allResources.find((r) => r.id === selectedReservation.resourceId)?.name ??
            'Resource'
          }
          buildUrl={buildUrl}
          database={database}
          onClose={() => setSelectedReservation(null)}
          onDelete={async () => {
            await applyCommand({
              type: 'DeleteResourceReservation',
              reservation_id: selectedReservation.id,
            } as any);
            setSelectedReservation(null);
          }}
        />
      )}
    </>
  );
};

export default ResourceReservationPanel;
